import { FunctionComponent, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

import { t } from "i18next";

import { Message } from "primereact/message";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { useSharedDialogState } from "../../../../Shared/Components/Dialogs/SharedDialog";
import { TextAreaInputField } from "../../../../Shared/Form/InputFields/TextAreaInput/TextAreaInputField";
import { TextInputField } from "../../../../Shared/Form/InputFields/TextInput/TextInputField";
import { IBugReportDialogState, IBugReportFormValues } from "../bugReportDialogTypes";

interface IBugReportDialogContentProps {
  maxSummaryLength?: number;
  maxDetailsLength?: number;
}

export const BugReportDialogContent: FunctionComponent<IBugReportDialogContentProps> = ({
  maxSummaryLength = 200,
  maxDetailsLength = 30000,
}: IBugReportDialogContentProps) => {
  const { sharedDialogState, setSharedDialogState } = useSharedDialogState<IBugReportDialogState>();

  const bugReportTranslationKeys = translationKeys.bugReport;

  const { control, formState } = useForm<IBugReportFormValues>({
    defaultValues: sharedDialogState.formData,
    mode: "all",
  });

  const watchedValues = useWatch({ control }) as IBugReportFormValues;

  useEffect(() => {
    const normalized: IBugReportFormValues = {
      summary: watchedValues?.summary ?? "",
      steps: watchedValues?.steps ?? "",
      actual: watchedValues?.actual ?? "",
      moreInfo: watchedValues?.moreInfo ?? "",
    };

    setSharedDialogState(previousState => ({
      ...previousState,
      isValid: formState.isValid,
      formData: normalized,
    }));
  }, [formState.isValid, watchedValues?.summary, watchedValues?.steps, watchedValues?.actual, watchedValues?.moreInfo]);

  return (
    <form>
      <div className="form-grid-layout form-grid-column-gap">
        <div className="form-grid-column-span-4">
          <TextInputField
            fieldName="summary"
            fieldNameTranslationKey={bugReportTranslationKeys.summaryLabel}
            rules={{ required: true, maxLength: maxSummaryLength }}
            control={control}
          />
        </div>

        <div className="form-grid-column-span-4">
          <TextAreaInputField
            fieldName="steps"
            fieldNameTranslationKey={bugReportTranslationKeys.stepsLabel}
            rules={{ required: true, maxLength: maxDetailsLength }}
            control={control}
          />
        </div>

        <div className="form-grid-column-span-4">
          <TextAreaInputField
            fieldName="actual"
            fieldNameTranslationKey={bugReportTranslationKeys.actualLabel}
            rules={{ required: true, maxLength: maxDetailsLength }}
            control={control}
          />
        </div>

        <div className="form-grid-column-span-4">
          <TextAreaInputField
            fieldName="moreInfo"
            fieldNameTranslationKey={bugReportTranslationKeys.moreInfoLabel}
            rules={{ maxLength: maxDetailsLength }}
            control={control}
          />
        </div>
      </div>

      <div className="bug-report-hints">
        <Message severity="info" text={t(bugReportTranslationKeys.imageHint)} />
        <Message severity="warn" text={t(bugReportTranslationKeys.gdprHint)} />
      </div>
    </form>
  );
};
